import { Component, ViewChild, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
  FormControl,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSort, MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import { MatOptionModule, MatNativeDateModule } from '@angular/material/core';
import isEqual from 'lodash/isEqual';

import { Subject, combineLatest, distinctUntilChanged, firstValueFrom, startWith, takeUntil, tap } from 'rxjs';
import { Store } from '@ngxs/store';

// Enums
import { BusinessSectorDescriptor } from '@app/enums/business-sector';
import { Country } from '@app/enums/country';
import { EntitySizeDescriptor, ESAdditionalInformationDescriptor } from '@app/enums/entity-size';
import { MotivationDescriptor } from '@app/enums/motivation';
import { ActorTypeDescriptor } from '@app/enums/actor-type';
import { IncidentTypeDescriptor } from '@app/enums/incident-type';
import { InputType } from '@app/enums/shared/input-type';
import { UserRole } from '@app/store/auth/userRole';

// Models
import { IncidentBaseModel, IncidentSortBy } from '@app/models/incident.model';

// Store
import { LoadIncidents } from '@app/store/app/states/incidents/incidents.actions';
import { IncidentsState } from '@app/store/app/states/incidents/incidents.state';
import { defaultIncidentsSearchFilters, IncidentsFilters } from '../../../types/incidents-filters';
import { AuthState } from '@app/store/auth/auth.state';

// Utils
import { mapEnumDescriptorToMultiselectOptions, sortAlphabetical } from '@app/utils/input-fields/input-fields.utils';

import {
  DEFAULT_PAGINATION_PAGE_NUMBER,
  DEFAULT_PAGINATION_PAGE_SIZE,
  DEFAULT_PAGINATION_TOTAL_ENTRIES,
} from '@app/config/defaults.config';

import {
  mapIncidentsFiltersToRouteParams,
  mapRouteParamsToIncidentsFilters,
} from '@app/utils/incidents/incidents.utils';
import { FilterHasValuePipe } from './pipes/filter-has-value-pipe';
import { SearchInDescriptor, SearchMode } from '@app/enums/search-mode';
import { AddIncidentModalComponent } from '@app/pages/incidents/add-incident-modal/add-incident-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { AddIncidentOptions } from '../models/add-incident-options-model';
import { createDate } from '@app/utils/datetime/datetime.utils';
import { AppDatePipe } from '../../../pipes/app-date.pipe';
import { IncidentStatus } from '@app/enums/incident-status';
import { AddIncidentViaUrlModalComponent } from '../add-incident-via-url-modal/add-incident-via-url-modal.component';
import { IncidentsService } from '@app/services/incidents/incidents.service';
import { GeoLocationSelectorComponent } from '@app/components/geo-location-selector/geo-location-selector.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { decimalValidator } from '@app/validators/decimal.validators';
import { DecimalErrorDirective } from '@app/shared/decimal-error.directive';

export type IncidentsTableColumn = 'title' | 'entity' | 'incidentDate' | 'status' | 'type';

@Component({
  standalone: true,
  selector: 'app-incidents-list-page',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatTableModule,
    MatSortModule,
    MatOptionModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTooltipModule,
    FilterHasValuePipe,
    AppDatePipe,
    GeoLocationSelectorComponent,
    DecimalErrorDirective,
  ],
  templateUrl: './incidents-list-page.component.html',
  styleUrl: './incidents-list-page.component.scss',
})
export class IncidentsListPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private userRole?: keyof typeof UserRole;

  protected readonly IncidentStatus = IncidentStatus;

  protected formGroup!: FormGroup;
  protected get countryCodesControl(): FormControl<Country[]> {
    return this.formGroup.get('countryCodes') as FormControl<Country[]>;
  }

  protected motivationOptions = mapEnumDescriptorToMultiselectOptions(MotivationDescriptor);
  protected businessSectorOptions = sortAlphabetical(mapEnumDescriptorToMultiselectOptions(BusinessSectorDescriptor));
  protected entitySizeOptions = mapEnumDescriptorToMultiselectOptions(EntitySizeDescriptor);
  protected entityTooltipDescriptor = Object.keys(ESAdditionalInformationDescriptor)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((k) => ({ [k]: (ESAdditionalInformationDescriptor as any)[k] }))
    .reduce((acc, next) => ({ ...acc, ...next }), {});

  protected actorTypeOptions = mapEnumDescriptorToMultiselectOptions(ActorTypeDescriptor);
  protected incidentTypeOptions = mapEnumDescriptorToMultiselectOptions(IncidentTypeDescriptor);
  protected searchInOptions = mapEnumDescriptorToMultiselectOptions(SearchInDescriptor);

  protected filters: IncidentsFilters = structuredClone(defaultIncidentsSearchFilters);
  protected filtersExpanded = false;

  protected totalEntries = DEFAULT_PAGINATION_TOTAL_ENTRIES;
  protected pageNumber = DEFAULT_PAGINATION_PAGE_NUMBER;
  protected pageSize = DEFAULT_PAGINATION_PAGE_SIZE;
  protected sortBy: string | undefined;
  protected sortDirection: SortDirection = '';

  protected get incidentsTableColumns(): IncidentsTableColumn[] {
    return this.isAdminUser
      ? ['title', 'entity', 'incidentDate', 'status', 'type']
      : ['title', 'entity', 'incidentDate'];
  }
  protected dataSource = new MatTableDataSource([] as IncidentBaseModel[]);

  protected get isAdminUser(): boolean {
    return this.userRole === UserRole.Admin;
  }

  private destroy$ = new Subject<void>();

  @ViewChild(MatSort) sort?: MatSort;
  @ViewChild(MatPaginator) paginator?: MatPaginator;

  constructor(
    private readonly incidentsService: IncidentsService,
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private readonly dialog: MatDialog,
  ) {
    this.initializeFormGroup();
  }

  public ngOnInit(): void {
    this.updateIncludeSubsidiariesAvailabilityOnDnbIdChange();

    combineLatest([
      this.store.select(IncidentsState.getIncidents),
      this.store.select(IncidentsState.getIncidentsPaginatedMetadata),
      this.store.select(AuthState.getAuthData),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([incidents, metadata, user]) => {
        this.dataSource.data = incidents;

        this.totalEntries = metadata.totalEntries;
        this.pageNumber = metadata.currentPage;
        this.pageSize = metadata.pageSize;

        if (this.paginator) {
          this.paginator.length = this.totalEntries;
          this.paginator.pageIndex = this.pageNumber - 1;
          this.paginator.pageSize = this.pageSize;
        }

        this.userRole = user.roles.includes(UserRole.Admin) ? UserRole.Admin : UserRole.User;
      });

    this.route.queryParams
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((a, b) => isEqual(a, b)), // Also emits when page gets new data - this way we hold it
        tap((queryParams) => {
          const { filters, pageNumber, pageSize, sortBy, sortDirection } =
            mapRouteParamsToIncidentsFilters(queryParams);
          this.filters = filters;
          this.applyFiltersToFormGroup();
          this.store.dispatch(new LoadIncidents({ filters, pageNumber, pageSize, sortBy, sortDirection }));
        }),
      )
      .subscribe();
  }

  public ngAfterViewInit(): void {
    this.dataSource.sort = this.sort as MatSort;

    if (this.paginator) {
      this.paginator.length = this.totalEntries;
      this.paginator.pageIndex = this.pageNumber - 1;
      this.paginator.pageSize = this.pageSize;
    }

    // Update filters when first loading (or reloading) the page
    const { filters } = mapRouteParamsToIncidentsFilters(this.route.snapshot.queryParams);
    this.filters = filters;
    this.applyFiltersToFormGroup();
    this.changeDetectorRef.detectChanges();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeFormGroup(): void {
    this.formGroup = this.formBuilder.group(
      {
        searchBy: [],
        searchIn: [],
        countryCodes: [],
        dateStart: [null],
        dateEnd: [null],
        motivation: [],
        businessSector: [],
        entitySize: [],
        actorTypes: [],
        revenueFrom: ['', [decimalValidator(17, 2)]],
        revenueTo: ['', [decimalValidator(17, 2)]],
        incidentDisabled: [],
        incidentTypes: [],
        dnbId: [],
        includeSubsidiaries: [],
      },
      {
        validators: [
          (control: AbstractControl): ValidationErrors | null => {
            const searchIn = control.get('searchIn')?.value as SearchMode[];
            const searchInControl = control.get('searchIn');

            if (control.get('searchBy')?.value && (!searchIn || searchIn.length === 0)) {
              // Set error on the control so mat-error can see it
              searchInControl?.setErrors({ invalidSearchMode: true });
            }

            return null;
          },
        ],
      },
    );
  }

  // public for testing purposes
  public async updateRouteParamsAndNavigateAsync(): Promise<void> {
    const routeParams = mapIncidentsFiltersToRouteParams(
      this.filters,
      this.pageNumber,
      this.pageSize,
      this.sortBy,
      this.sortDirection,
    );
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { ...routeParams, refresh: Date.now() },
      queryParamsHandling: 'merge',
    });
  }

  private applyFiltersToFormGroup(): void {
    const hasDnbId = this.hasDnbIdValue(this.filters.dnbId.value);
    this.filters.includeSubsidiaries.value = hasDnbId ? this.filters.includeSubsidiaries.value : false;

    this.formGroup.patchValue({
      searchBy: this.filters.searchBy.value,
      searchIn: this.filters.searchIn.value,
      countryCodes: this.filters.countryCodes.value,
      dateStart: createDate(this.filters.dateStart.value),
      dateEnd: createDate(this.filters.dateEnd.value),
      motivation: this.filters.motivation.value,
      businessSector: this.filters.businessSector.value,
      entitySize: this.filters.entitySize.value,
      actorTypes: this.filters.actorTypes.value,
      revenueFrom: this.filters.revenueFrom.value,
      revenueTo: this.filters.revenueTo.value,
      incidentDisabled: this.isAdminUser ? (this.filters.incidentDisabled?.value ?? 'all') : void 0,
      incidentTypes: this.isAdminUser ? this.filters.incidentTypes.value : [],
      dnbId: this.filters.dnbId.value,
      includeSubsidiaries: hasDnbId ? this.filters.includeSubsidiaries.value : false,
    });
  }

  protected onDnbIdInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitizedValue = input.value.replace(/\D/g, '');

    if (input.value === sanitizedValue) {
      return;
    }

    input.value = sanitizedValue;
    this.formGroup.get('dnbId')?.setValue(sanitizedValue);
  }

  protected async addIncidentClickedAsync(): Promise<void> {
    const dialogRef = this.dialog.open<AddIncidentModalComponent, AddIncidentOptions, AddIncidentOptions | null>(
      AddIncidentModalComponent,
      {
        width: '90vw',
        maxWidth: '560px',
        disableClose: true,
      },
    );

    const dialogResult = await firstValueFrom(dialogRef.afterClosed());
    switch (dialogResult) {
      case AddIncidentOptions.Url:
        await this.createIncidentByUrlAndNavigateAsync();
        break;
      case AddIncidentOptions.Manual:
        this.router.navigate(['create'], {
          relativeTo: this.route,
        });
        break;
    }
  }

  private async createIncidentByUrlAndNavigateAsync(): Promise<void> {
    const dialogRef = this.dialog.open<AddIncidentViaUrlModalComponent, string, string | null>(
      AddIncidentViaUrlModalComponent,
      {
        width: '90vw',
        maxWidth: '560px',
        disableClose: true,
      },
    );

    const incidentUrl = await firstValueFrom(dialogRef.afterClosed());

    if (!incidentUrl) {
      // Operation was cancelled
      return;
    }

    const pendingIncidentId = await firstValueFrom(
      this.incidentsService.createPendingIncident({
        url: incidentUrl,
      }),
    );

    // We have a created incident with status - pending. We can go to the incident details.
    await this.router.navigate([`/incidents/${pendingIncidentId}`]);
  }

  //#region Filters
  protected clearIndividualFilter(event: MouseEvent, controlName: string): void {
    event.preventDefault();
    event.stopImmediatePropagation();

    const control = this.formGroup.get(controlName);

    if (!control) {
      return;
    }

    if (controlName === 'dateStart' || controlName === 'dateEnd') {
      control.setValue(void 0);
      return;
    }

    control.setValue(void 0);
  }

  protected async onSearchAsync(): Promise<void> {
    if (this.formGroup.invalid) {
      return;
    }

    this.applyFormGroupToFilters();
    this.pageNumber = DEFAULT_PAGINATION_PAGE_NUMBER;
    await this.updateRouteParamsAndNavigateAsync();
  }

  private applyFormGroupToFilters(): void {
    this.filters = Object.keys(this.filters)
      .map((key) => {
        const entity = this.filters[key as keyof IncidentsFilters];
        const control = this.formGroup.get(key);

        if (!entity || !control) {
          return {};
        }

        const formValue = control.value;

        if (entity.type === InputType.Text) {
          return { [key]: { ...entity, value: formValue ?? '' } };
        }

        if (entity.type === InputType.Select) {
          return { [key]: { ...entity, value: this.formGroup.value[key] ?? '' } };
        }

        if (entity.type === InputType.Checkbox) {
          return { [key]: { ...entity, value: formValue ?? false } };
        }

        if (entity.type === InputType.Multiselect) {
          if (key === 'countryCodes') {
            const formControl = this.formGroup.get(key);
            return { [key]: { ...entity, value: formControl?.value ?? [] } };
          }
          return { [key]: { ...entity, value: this.formGroup.value[key] ?? '' } };
        }

        return {};
      })
      .reduce((acc: {}, next: {}) => ({ ...acc, ...next }), {}) as unknown as IncidentsFilters;
  }

  protected async onResetAsync(): Promise<void> {
    this.filters = structuredClone(defaultIncidentsSearchFilters);
    this.pageNumber = DEFAULT_PAGINATION_PAGE_NUMBER;
    await this.updateRouteParamsAndNavigateAsync();
  }

  private updateIncludeSubsidiariesAvailabilityOnDnbIdChange(): void {
    const dnbIdControl = this.formGroup.get('dnbId');
    const includeSubsidiariesControl = this.formGroup.get('includeSubsidiaries');

    if (!dnbIdControl || !includeSubsidiariesControl) {
      return;
    }

    dnbIdControl.valueChanges.pipe(startWith(dnbIdControl.value), takeUntil(this.destroy$)).subscribe((dnbId) => {
      if (this.hasDnbIdValue(dnbId)) {
        includeSubsidiariesControl.enable({ emitEvent: false });
        return;
      }

      includeSubsidiariesControl.setValue(false, { emitEvent: false });
      includeSubsidiariesControl.disable({ emitEvent: false });
    });
  }

  private hasDnbIdValue(dnbId: unknown): boolean {
    return dnbId !== null && dnbId !== undefined && dnbId !== '';
  }

  //#endregion Filters

  //#region Data Table
  protected async onPageChangeAsync(event: PageEvent): Promise<void> {
    this.pageNumber = event.pageIndex + 1;
    this.pageSize = event.pageSize;

    await this.updateRouteParamsAndNavigateAsync();
  }

  protected async onSortChangeAsync(sortInfo: Sort): Promise<void> {
    if (!sortInfo.active || sortInfo.direction === '') {
      this.sortBy = undefined;
    } else {
      const columnName = sortInfo.active as IncidentsTableColumn;

      // Mapping from mat table column definition names to backend enum value
      if (columnName === 'entity') {
        this.sortBy = IncidentSortBy.EntityName.toString();
      } else if (columnName === 'title') {
        this.sortBy = IncidentSortBy.Title.toString();
      } else if (columnName === 'incidentDate') {
        this.sortBy = IncidentSortBy.StartedAt.toString();
      } else if (columnName === 'status') {
        this.sortBy = IncidentSortBy.IsReviewed.toString();
      } else {
        console.error('Invalid column for sorting');
        return;
      }
    }

    this.sortDirection = sortInfo.direction;
    this.pageNumber = 1; // Reset to first page when sorting changes
    await this.updateRouteParamsAndNavigateAsync();
  }
  //#endregion Data Table
}
